import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { adminApi, type ApiKey } from '../lib/api.js';
import { useAction } from '../lib/useAsync.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { KeyReveal } from '../components/KeyReveal.js';
import { Button, HoldButton, Note, OutcomeNote } from '../components/ui.js';
import { exact } from '../lib/format.js';

/**
 * A new secret for an existing credential: confirm, then show it once.
 *
 * Two steps in one dialog. The old secret keeps working for twelve hours after
 * the press (RidgeWill/auth#649), so a rotation no longer breaks anything at
 * once, but the reveal is still the only time the new one is readable: asking
 * first gives somebody the chance to have the place they will paste it open.
 *
 * Shared by the Credentials screen and an agent's record. It began on the
 * agent record only, so a service key could be revoked or re-issued from the
 * Credentials screen but not rotated, and replacing one meant re-creating its
 * grants by hand (RidgeWill/platform, Railway disclosure, 2026-10-23).
 */
export function RotateCredential({ apiKey, onClose }: { apiKey: ApiKey; onClose: () => void }) {
  const action = useAction();
  const [rotated, setRotated] = useState<{ key: string; oldUntil?: string } | null>(null);

  if (rotated) {
    return (
      <Modal
        title="New secret"
        subtitle="Copy it now. This is the only time it is readable."
        onClose={onClose}
        footer={
          <ModalActions>
            <Button onClick={onClose}>Done</Button>
          </ModalActions>
        }
      >
        <KeyReveal value={rotated.key} />
        {rotated.oldUntil && (
          <Note>
            The previous secret keeps working until {exact(rotated.oldUntil)}. Update everything
            that uses it before then, or revoke the key if it has to stop now.
          </Note>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title={`Rotate ${apiKey.name ?? 'this key'}`}
      onClose={onClose}
      width={460}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <HoldButton
            title="Hold for a second and a half to rotate. The current secret keeps working for 12 hours."
            disabled={action.running}
            onHold={() =>
              void action.run(async () => {
                const r = await adminApi.rotateApiKey(apiKey.id);
                setRotated({ key: r.key, oldUntil: r.previousSecretValidUntil });
                return 'Rotated.';
              })
            }
          >
            {action.running ? 'Rotating…' : 'Rotate now'}
          </HoldButton>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <p style={para}>
        You get a new secret. The current one keeps working for 12 hours, so there is time to put the
        new one everywhere that uses it. To stop the current secret right away, revoke the key instead.
      </p>
    </Modal>
  );
}

/**
 * Whether the auth service will rotate this key at all.
 *
 * It refuses a key that names no application (`NOT_A_SERVICE_CREDENTIAL`):
 * such a key is self-service, and every internal route refuses it whatever it
 * holds, so a new secret for it would change nothing. Offering the button and
 * letting it fail would be the same mistake in the other direction.
 */
export function canRotateCredential(k: ApiKey): boolean {
  const slug = k.metadata?.appSlug;
  return k.enabled !== false && typeof slug === 'string' && slug !== '';
}

const para = { margin: 0, fontSize: '.86rem', color: C.textMid, lineHeight: 1.5 };
